import classNames from "classnames";
import React from "react";
import useTranslations from "../../hooks/useTranslations";
import { LinkInfo } from "../../models/shared/LinkInfo";
import { isNullOrWhiteSpace } from "../../shared/string";
import { formatPhoneNumberLink, isValidLink } from "../../utilities/links";
import Button, { ButtonSize } from "../Buttons/Button";
import PortableText, { PortableTextBody } from "../PortableText";
import { renderLinkTitle } from "../SanityLink";
import { getHelpCardColorTheme, HelpCardColorTheme, HelpCardType } from "./HelpCard";
import HelpCardContainer, { Props as ContainerProps } from "./HelpCardContainer";

export type SanityHelpCardCta = {
  // Sanity-schema sourced props
  _type: HelpCardType.Cta;
  title: string;
  portableTextContent?: PortableTextBody;
  _rawPortableTextContent?: PortableTextBody;
  button?: LinkInfo;
  phoneNumber?: string;
};

export interface Props extends ContainerProps, SanityHelpCardCta {
  rightPaddedTitle?: boolean;
  removeButtonXPadding?: boolean;
  buttonTransparent?: boolean;
}

const HelpCardCta = (props: Props) => {
  const {
    _rawPortableTextContent,
    button,
    className = "",
    phoneNumber,
    portableTextContent,
    removeButtonXPadding,
    rightPaddedTitle,
    title,
    ...containerProps
  } = props;

  const { buttonColor, textColor, hrColor } = getHelpCardColorTheme(
    containerProps.colorTheme ?? HelpCardColorTheme.Default
  );

  const translations = useTranslations();

  const buttonFallbackText = translations.sendMessage;

  return (
    <HelpCardContainer
      className={className}
      roundedClassName="rounded-lg"
      {...containerProps}
    >
      {!isNullOrWhiteSpace(title) && (
        <>
          <h3
            className={classNames("font-bold mb-4", textColor, {
              "pr-16 lg:pr-0": rightPaddedTitle
            })}
          >
            {title}
          </h3>
          <hr className={classNames("h-divider-thick w-16 mb-8 lg:mb-10", hrColor)} />
        </>
      )}

      {(_rawPortableTextContent || portableTextContent) && (
        <PortableText
          _rawBody={_rawPortableTextContent ?? portableTextContent}
          addSections={false}
          elementClasses={{
            p: classNames("text-xl font-bold mb-8", textColor),
            ul: classNames("text-xl mb-8", textColor),
            li: "mb-4 last:mb-0 text-xl"
          }}
        />
      )}

      <div
        className={classNames("flex flex-col items-center lg:flex-row mt-auto", {
          "px-6 lg:px-0": !removeButtonXPadding
        })}
      >
        {isValidLink(button) ? (
          <Button
            className="mb-4 lg:mb-0 text-center"
            color={buttonColor}
            size={ButtonSize.Normal}
            type={"link"}
            {...button}
          >
            {renderLinkTitle(button) ?? buttonFallbackText}
          </Button>
        ) : (
          <Button
            className="mb-4 lg:mb-0 text-center"
            color={buttonColor}
            isExternalLink
            size={ButtonSize.Normal}
            type={"link"}
            url="#form"
          >
            {buttonFallbackText}
          </Button>
        )}

        {!isNullOrWhiteSpace(phoneNumber) && (
          <div
            className={classNames(
              "flex flex-col justify-center lg:ml-12 font-bold text-center",
              textColor
            )}
          >
            <div className="uppercase text-sm">{translations.orCall}</div>

            <div>
              <a
                href={formatPhoneNumberLink(phoneNumber)}
                className="text-2xl text-current no-underline hover:opacity-90 hover:underline focus:outline-none focus:shadow-outline-primary"
              >
                {phoneNumber}
              </a>
            </div>
          </div>
        )}
      </div>
    </HelpCardContainer>
  );
};

export default HelpCardCta;
